import type { MonitoringEventsPage, MonitoringEventsQuery } from './monitoringEvents';
import type { RadarOutcomeUsage } from './radarUsageModel';
import type { MonitoringAdviceResponse } from './monitoringAdvice';
import { radarAnalytics } from './radarAnalytics';
import { optionalRequestId } from '@/lib/requestId';
import { networkFailureKind, reportBrowserFailure } from '@/monitoring/browserErrors';
import type {
  RadarActState, RadarReveal, RadarDraft, RadarDraftInput, OutreachBatchInput,
  MarketBrief,
  RadarSession,
  RadarWorkspace,
  RadarWorkspaceBundle,
  RadarWatch, SignalFocus, SignalPlan,
} from './types';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000';

export type ResearchSettings = {
  source_mode:'open_search'|'seed_list'; active_list_id:string|null;
  routes:Record<'company_research'|'contacts'|'draft','api'|'subscription'>;
};
export type ResearchState = {
  available:boolean; internal?:boolean; settings:ResearchSettings; subscription_model?:string;
  lists?:{id:string;name:string;company_count:number;processed_count:number}[];
  recent_outcomes?:{seed_id:string;company_name:string;source_url:string;
    verdict:'fit'|'not_fit'|'insufficient_information';reason:string}[];
};
export type CampaignProfile = Pick<OutreachBatchInput,'sender_name'|'seller_company'|'claims'|'objective'|'signature'|'language'|'tone'> & {email_template:string};
export type CampaignStart = {id:string;list_id:string;profile:CampaignProfile;delivery:'gmail_drafts';outreach_mode:'intro'|'buyer_led';
  require_buyer_matches:boolean;template_approved:true;gmail_account_email:string};
export type ListCampaign = {id:string;list_id:string;list_name:string;status:string;pause_reason:string|null;
  total_count:number;processed_count:number;draft_count:number;saved_count:number;cancelled_count:number;
  profile:Partial<CampaignProfile>;items:{ordinal:number;company_name:string;status:string;outcome?:string;delivery_state?:string;saved_to_gmail:boolean}[];next_offset:number|null};

const routeSegments = new Set(['api', 'radar', 'session', 'workspaces', 'act', 'contacts',
  'reveal', 'drafts', 'gmail', 'translations', 'signals', 'feedback', 'watches', 'confirm',
  'signal-plan', 'brief-hypothesis', 'brief-review', 'brief', 'search-runs', 'email-signals', 'refresh',
  'monitoring-events', 'companies', 'monitoring-advice', 'outreach', 'contact-ledger', 'suppression', 'google-grant', 'abandon',
  'expo', 'sessions', 'email', 'claim', 'retry-search', 'retry-mail']);
const actionNames: Record<string, string> = {
  'brief-hypothesis': 'brief_generate', 'brief-review': 'brief_review', brief: 'brief_confirm', 'search-runs': 'search',
  reveal: 'reveal', drafts: 'draft_generate', gmail: 'gmail_export', watches: 'watch_add',
  confirm: 'watch_confirm', 'signal-plan': 'watch_focus', feedback: 'feedback',
};
function routeTemplate(path: string): string {
  return path.split('?')[0].split('/').map(part => !part || routeSegments.has(part) ? part : ':id').join('/');
}
function safeCode(value: unknown): string {
  return typeof value === 'string' && /^[a-z][a-z0-9_]{0,63}$/.test(value) ? value : 'unknown';
}
export async function radarRequest<T>(path: string, token: string, init: RequestInit = {}): Promise<T> {
  const method = init.method || 'GET';
  const customHeaders = new Headers(init.headers);
  const actionId = customHeaders.get('X-Radar-Action-Id') || customHeaders.get('Idempotency-Key')
    || (method !== 'GET' ? optionalRequestId() : undefined);
  const sessionId = radarAnalytics.sessionId();
  const context = radarAnalytics.context();
  const route = routeTemplate(path);
  const workspaceId = path.match(/\/workspaces\/([^/?]+)/)?.[1];
  const parts = path.split('/');
  const last = parts[parts.length - 1];
  const action = actionNames[last] || (route.endsWith('/drafts/:id') ? 'draft_save'
    : route.endsWith('/watches/:id') ? 'watch_status' : undefined);
  if (actionId && action && method !== 'GET') {
    radarAnalytics.once('radar_action_started', actionId, {
      workspace_id: workspaceId, action_id: actionId, action, method,
    });
  }
  let response: Response;
  try {
    response = await fetch(`${API_BASE_URL}${path}`, {
      ...init,
      headers: {
        'Content-Type': 'application/json', Authorization: `Bearer ${token}`,
        ...Object.fromEntries(customHeaders),
        ...(actionId ? { 'X-Radar-Action-Id': actionId } : {}),
        ...(sessionId ? { 'X-PostHog-Session-Id': sessionId } : {}),
      },
    });
  } catch (error) {
    if (!init.signal?.aborted) reportBrowserFailure({ stage: 'radar_request', kind: networkFailureKind(error), route, method });
    radarAnalytics.capture('radar_api_failed', { workspace_id: workspaceId, action_id: actionId,
      route, method, status: 0, error_code: 'network_error' }, context);
    throw error;
  }
  if (!response.ok) {
    const body = await response.json().catch(() => null);
    const code = safeCode(body?.detail?.error?.code || body?.error?.code);
    const rawRequestId = response.headers?.get('X-Request-Id');
    const requestId = rawRequestId && /^[a-f0-9-]{36}$/i.test(rawRequestId) ? rawRequestId : undefined;
    if (response.status >= 500 || response.status === 408 || response.status === 429) {
      reportBrowserFailure({ stage: 'radar_request', kind: 'http', status: response.status, route, method, requestId });
    }
    radarAnalytics.capture('radar_api_failed', { workspace_id: workspaceId, action_id: actionId,
      route, method, status: response.status, error_code: code, request_id: requestId }, context);
    const error = new Error(body?.detail?.error?.message || body?.error?.message || 'Buyer Radar request failed.');
    Object.assign(error, { status: response.status, code, requestId });
    throw error;
  }
  try { return await response.json(); }
  catch (error) {
    reportBrowserFailure({ stage: 'radar_request', kind: 'invalid_response', status: response.status, route, method });
    throw error;
  }
}
const request = radarRequest;

export const radarApi = {
  emailSettings(token:string,workspaceId:string) {
    return request<{reference_email:string}>(`/api/radar/workspaces/${workspaceId}/email-settings`,token,{cache:'no-store'});
  },
  saveEmailSettings(token:string,workspaceId:string,reference_email:string) {
    return request<{reference_email:string}>(`/api/radar/workspaces/${workspaceId}/email-settings`,token,{method:'PUT',body:JSON.stringify({reference_email})});
  },
  previewReferenceEmail(token:string,workspaceId:string,input:{id:string;candidate_id:string}) {
    return request<{subject:string;body:string;company_name:string}>(`/api/radar/workspaces/${workspaceId}/email-preview`,token,{method:'POST',body:JSON.stringify(input)});
  },
  prepareReferenceEmails(token:string,workspaceId:string,input:import('./types').ReferencePrepareInput) {
    return request<import('./types').ReferencePrepareResult>(`/api/radar/workspaces/${workspaceId}/reference-outreach`,token,{method:'POST',body:JSON.stringify(input)});
  },
  usage(token:string,workspaceId:string) {
    return request<RadarOutcomeUsage|null>(`/api/radar/workspaces/${workspaceId}/usage`,token);
  },
  researchSettings(token: string, workspaceId: string) {
    return request<ResearchState>(`/api/radar/workspaces/${workspaceId}/research-settings`,token);
  },
  saveResearchSettings(token: string, workspaceId: string, settings: ResearchSettings) {
    return request<ResearchState>(`/api/radar/workspaces/${workspaceId}/research-settings`,token,
      {method:'PUT',body:JSON.stringify(settings)});
  },
  importResearchList(token: string, workspaceId: string, name: string, csv: string) {
    return request<{id:string;name:string;company_count:number}>(`/api/radar/workspaces/${workspaceId}/research-lists`,token,
      {method:'POST',body:JSON.stringify({name,csv})});
  },
  resumeResearch(token: string, workspaceId: string) {
    return request<{search_run_ids:string[];batch_ids:string[]}>(`/api/radar/workspaces/${workspaceId}/research-resume`,token,{method:'POST'});
  },
  listCampaign(token:string,workspaceId:string,listId:string) {
    return request<ListCampaign|null>(`/api/radar/workspaces/${workspaceId}/research-campaigns?list_id=${encodeURIComponent(listId)}`,token);
  },
  startListCampaign(token:string,workspaceId:string,input:CampaignStart) {
    return request<ListCampaign>(`/api/radar/workspaces/${workspaceId}/research-campaigns`,token,{method:'POST',body:JSON.stringify(input)});
  },
  controlListCampaign(token:string,workspaceId:string,campaignId:string,action:'pause'|'resume'|'cancel') {
    return request<ListCampaign>(`/api/radar/workspaces/${workspaceId}/research-campaigns/${campaignId}/control`,token,{method:'POST',body:JSON.stringify({action})});
  },
  monitoringEvents(token: string, workspaceId: string, query: MonitoringEventsQuery) {
    const params = new URLSearchParams();
    for (const [key, value] of Object.entries(query)) if (value !== undefined) params.set(key, String(value));
    return request<MonitoringEventsPage>(`/api/radar/workspaces/${workspaceId}/monitoring-events?${params}`, token, {cache:'no-store'});
  },
  monitoringAdvice(token: string, workspaceId: string, companyId: string) {
    return request<MonitoringAdviceResponse>(`/api/radar/workspaces/${encodeURIComponent(workspaceId)}/companies/${encodeURIComponent(companyId)}/monitoring-advice`, token, {cache:'no-store'});
  },
  dailyOutreach(token:string,workspaceId:string) {
    return request<DailyOutreachState>(`/api/radar/workspaces/${workspaceId}/daily-outreach`,token);
  },
  saveDailyOutreach(token:string,workspaceId:string,input:{enabled:boolean;local_time?:string;max_drafts?:number;profile?:DailyProfile}) {
    return request<DailyOutreachState>(`/api/radar/workspaces/${workspaceId}/daily-outreach`,token,{method:'POST',body:JSON.stringify(input)});
  },
  async outreach(token: string, workspaceId: string, page?:{offset?:number;company?:string}) {
    const query=new URLSearchParams();if(page?.offset)query.set('offset',String(page.offset));if(page?.company)query.set('company',page.company);
    const path=`/api/radar/workspaces/${workspaceId}/outreach${query.size?'?'+query:''}`;
    try { return await request<import('./types').OutreachState>(path,token,{cache:'no-store'}); }
    catch(error) {
      // This endpoint only reads saved state. Never replay preparation, Gmail
      // exports or reconciliation POSTs when their outcome may be unknown.
      const status=(error as {status?:number})?.status;
      if(!(error instanceof TypeError) && ![502,503,504].includes(status||0))throw error;
      await new Promise(resolve=>setTimeout(resolve,500));
      return request<import('./types').OutreachState>(path,token,{cache:'no-store'});
    }
  },
  retryOutreachSave(token:string,workspaceId:string,batchId:string) {
    return request<{queued:boolean}>(`/api/radar/workspaces/${workspaceId}/outreach/${batchId}/retry-save`,token,{method:'POST'});
  },
  prepareOutreach(token: string, workspaceId: string, input: OutreachBatchInput) {
    return request<{id:string;queued_count?:number;skipped?:{candidate_id:string;reason:string}[];history_check?:ContactCheck}>(`/api/radar/workspaces/${workspaceId}/outreach`,token,
      {method:'POST',body:JSON.stringify(input)});
  },
  contactLedger(token: string, workspaceId: string) {
    return request<ContactLedgerState>(`/api/radar/workspaces/${workspaceId}/contact-ledger`, token);
  },
  // Without a browser token the server uses the workspace's stored Gmail connection.
  refreshContactLedger(token: string, workspaceId: string, accessToken?: string) {
    return request<ContactLedgerState>(`/api/radar/workspaces/${workspaceId}/contact-ledger/refresh`, token,
      {method:'POST',body:JSON.stringify(accessToken?{access_token:accessToken}:{})});
  },
  connectContactGmail(token: string, workspaceId: string, code: string) {
    return request<ContactLedgerState>(`/api/radar/workspaces/${workspaceId}/contact-ledger/google-grant`, token,
      // Google's popup code flow expects this header on the relay to the server.
      {method:'POST',headers:{'X-Requested-With':'XmlHttpRequest'},body:JSON.stringify({code})});
  },
  disconnectContactGmail(token: string, workspaceId: string) {
    return request<ContactLedgerState>(`/api/radar/workspaces/${workspaceId}/contact-ledger/google-grant`, token, {method:'DELETE'});
  },
  abandonContactDraft(token: string, workspaceId: string, entryId: string) {
    return request<ContactLedgerState>(`/api/radar/workspaces/${workspaceId}/contact-ledger/${entryId}/abandon`, token, {method:'POST'});
  },
  setContactSuppression(token: string, workspaceId: string, input: {kind:'email'|'company';key:string;reason:'opt_out'|'manual'|'existing_relationship';suppressed:boolean}) {
    return request<ContactLedgerState>(`/api/radar/workspaces/${workspaceId}/contact-ledger/suppression`, token,
      {method:'POST',body:JSON.stringify(input)});
  },
  emailSignals(token: string, workspaceId: string) {
    return request<EmailSignalsState>(`/api/radar/workspaces/${workspaceId}/email-signals`, token);
  },
  refreshEmailSignals(token: string, workspaceId: string, accessToken: string) {
    return request<EmailSignalsState>(`/api/radar/workspaces/${workspaceId}/email-signals/refresh`, token,
      {method:'POST',body:JSON.stringify({access_token:accessToken})});
  },
  gmailDraft(token: string, workspaceId: string, draftId: string, version: number, accessToken: string, accountEmail: string) {
    return request<{gmail_draft_id: string; account_email: string; url: string}>(`/api/radar/workspaces/${workspaceId}/drafts/${draftId}/gmail`, token,
      { method: 'POST', body: JSON.stringify({version,access_token:accessToken,account_email:accountEmail}) });
  },
  act(token: string, workspaceId: string) {
    return request<RadarActState>(`/api/radar/workspaces/${workspaceId}/act`, token);
  },
  reveal(token: string, workspaceId: string, contactId: string) {
    return request<RadarReveal>(`/api/radar/workspaces/${workspaceId}/contacts/${contactId}/reveal`, token, { method: 'POST' });
  },
  createDraft(token: string, workspaceId: string, input: RadarDraftInput) {
    return request<RadarDraft>(`/api/radar/workspaces/${workspaceId}/drafts`, token, { method: 'POST', headers: { 'X-Radar-Action-Id': input.id }, body: JSON.stringify(input) });
  },
  saveDraft(token: string, workspaceId: string, draft: RadarDraft) {
    return request<RadarDraft>(`/api/radar/workspaces/${workspaceId}/drafts/${draft.id}`, token, {
      method: 'PATCH', body: JSON.stringify({ version: draft.version, subject: draft.subject, body: draft.body }),
    });
  },
  saveOutreachDraft(token: string, workspaceId: string, draft: Pick<RadarDraft,'id'|'version'|'subject'|'body'>) {
    return request<Pick<RadarDraft,'id'|'version'|'subject'|'body'>>(`/api/radar/workspaces/${workspaceId}/drafts/${draft.id}`,token,
      {method:'PATCH',body:JSON.stringify({version:draft.version,subject:draft.subject,body:draft.body})});
  },
  translate(token: string, workspaceId: string, locale: 'en' | 'zh-Hans' | 'zh-Hant', texts: string[]) {
    return request<{ translations: string[] }>(`/api/radar/workspaces/${workspaceId}/translations`, token,
      { method: 'POST', body: JSON.stringify({ locale, texts }) });
  },
  signalFeedback(token: string, workspaceId: string, signalId: string, verdict: 'useful' | 'not_relevant') {
    return request(`/api/radar/workspaces/${workspaceId}/signals/${signalId}/feedback`, token, {
      method: 'POST', body: JSON.stringify({ verdict }),
    });
  },

  confirmWatches(token: string, workspaceId: string, watchIds: string[]) {
    return request<RadarWatch[]>(`/api/radar/workspaces/${workspaceId}/watches/confirm`, token, {
      method: 'POST', body: JSON.stringify({ watch_ids: watchIds }),
    });
  },

  addWatch(token: string, workspaceId: string, candidateId: string) {
    return request<RadarWatch>(`/api/radar/workspaces/${workspaceId}/watches`, token,
      { method: 'POST', body: JSON.stringify({ candidate_id: candidateId }) });
  },
  setWatchStatus(token: string, workspaceId: string, watchId: string, status: 'active' | 'paused') {
    return request<RadarWatch>(`/api/radar/workspaces/${workspaceId}/watches/${watchId}`, token,
      { method: 'PATCH', body: JSON.stringify({ status }) });
  },
  updateFocus(token: string, workspaceId: string, focus: SignalFocus) {
    return request<SignalPlan>(`/api/radar/workspaces/${workspaceId}/signal-plan`, token,
      { method: 'PATCH', body: JSON.stringify(focus) });
  },
  openSession(token: string, workspaceId?:string) {
    return request<RadarSession>('/api/radar/session'+(workspaceId?'?workspace_id='+encodeURIComponent(workspaceId):''), token, { method: 'POST' });
  },

  getWorkspace(token: string, workspaceId: string, briefVersion?: number | null) {
    return request<RadarWorkspaceBundle>(`/api/radar/workspaces/${workspaceId}?contact_channels=1${briefVersion?`&brief_version=${briefVersion}`:''}`, token);
  },

  generateBrief(
    token: string,
    workspaceId: string,
    input: {
      seller_url?: string;
      target_market?: string;
      relationship_goal?: string;
      seller_notes?: string;
    }
  ) {
    return request<RadarWorkspace>(
      `/api/radar/workspaces/${workspaceId}/brief-hypothesis`,
      token,
      { method: 'POST', body: JSON.stringify(input) }
    );
  },

  reviewBrief(token: string, workspaceId: string, brief: MarketBrief) {
    return request<{ brief: MarketBrief; changed: boolean }>(`/api/radar/workspaces/${workspaceId}/brief-review`, token, {
      method: 'POST', body: JSON.stringify(brief),
    });
  },

  confirmBrief(token: string, workspaceId: string, brief: MarketBrief) {
    return request<RadarWorkspace>(`/api/radar/workspaces/${workspaceId}/brief`, token, {
      method: 'PATCH',
      body: JSON.stringify(brief),
    });
  },

  async startSearch(token: string, workspaceId: string, idempotencyKey: string, briefVersion?: number | null) {
    const context = radarAnalytics.context();
    radarAnalytics.beginSearch(idempotencyKey, workspaceId);
    const run = await request<{ id: string; status: string }>(
      `/api/radar/workspaces/${workspaceId}/search-runs${briefVersion?`?brief_version=${briefVersion}`:''}`,
      token,
      { method: 'POST', headers: { 'Idempotency-Key': idempotencyKey } }
    );
    radarAnalytics.bindSearch(idempotencyKey, run.id, context);
    return run;
  },

  saveFeedback(
    token: string,
    workspaceId: string,
    candidateId: string,
    payload: { verdict: 'relevant' | 'not_fit'; reason_codes: string[]; note?: string }
  ) {
    return request(
      `/api/radar/workspaces/${workspaceId}/candidates/${candidateId}/feedback`,
      token,
      { method: 'POST', body: JSON.stringify(payload) }
    );
  },

  undoFeedback(token: string, workspaceId: string, candidateId: string) {
    return request(
      `/api/radar/workspaces/${workspaceId}/candidates/${candidateId}/feedback`,
      token,
      { method: 'DELETE' }
    );
  },
};

export type ContactCheck = {checked:number;failed?:number;complete:boolean;reason?:'reconnect_required';source:'background_grant'|'browser'|'not_connected'|'check_failed'};
export type ContactLedgerState = {enabled:boolean;client_id?:string|null;
  background?:{connected:boolean;account_email:string|null;status:'active'|'invalid'|null;last_checked_at:string|null};
  check?:ContactCheck;items:Array<{
  id:string;kind:'email'|'company';key:string;company_key:string|null;company_name:string|null;
  status:'drafted'|'abandoned'|'sent'|'replied'|'bounced'|'suppressed'|null;abandoned_at?:string|null;origin:'radar'|'import'|'manual';note:string;
  drafted_at:string|null;sent_at:string|null;replied_at:string|null;bounced_at:string|null;suppressed_at:string|null;
  suppress_reason:string|null;last_checked_at:string|null;sync_error:string|null;account_email:string|null;
  verified_drafts?:Array<{draft_id:string;draft_version:number;account_email:string;checked_at:string}>;
}>};
export type EmailSignalsState = {enabled:boolean;items:Array<{
  id:string;draft_id:string;draft_version:number;subject:string;recipient:string;account_email:string;
  status:'pending'|'observed'|'none'|'unavailable';signal_at:string|null;sent_at:string|null;
  last_checked_at:string|null;sync_error:string|null;
}>};

export type DailyProfile = Omit<OutreachBatchInput,'id'|'candidate_ids'|'retry_research'|'save_to_gmail'|'gmail_account_email'>;
export type DailyOutreachState = {
  available:boolean; draft_access:boolean; account_email:string|null; timezone:string;client_id?:string|null;
  settings:{enabled:boolean;local_time:string;max_drafts:number;profile:DailyProfile}|null;
  latest_run:{id:string;run_date:string;status:string;result:{reason?:string;saved_count?:number};updated_at:string}|null;
};
